import React from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const HotspotList = ({ hotspots, onSelect, onDelete, onToggleVisibility, onCopy, selectedId, onNavigateToPage }) => {
  const { t } = useTranslation();
  const getHotspotIcon = (type) => {
    switch (type) {
      case 'buy':
        return 'ShoppingCart';
      case 'link':
        return 'Link';
      case 'info':
        return 'Info';
      case 'image':
        return 'Image';
      case 'video':
        return 'Video';
      case 'internal_link':
        return 'ArrowRight';
      default:
        return 'MapPin';
    }
  };

  const getHotspotColor = (type) => {
    switch (type) {
      case 'buy':
        return 'bg-green-500';
      case 'link':
        return 'bg-blue-500';
      case 'info':
        return 'bg-purple-500';
      case 'image':
        return 'bg-orange-500';
      case 'video':
        return 'bg-red-500';
      case 'internal_link':
        return 'bg-teal-500';
      default:
        return 'bg-gray-500';
    }
  };

  const getHotspotLabel = (hotspot) => {
    if (hotspot?.title) return hotspot?.title;
    if (hotspot?.url) return t('catalogEditor.hotspotList.labelExternal');
    if (hotspot?.sku) return `SKU: ${hotspot?.sku}`;
    return `${hotspot?.type?.charAt(0)?.toUpperCase() + hotspot?.type?.slice(1)} Hotspot`;
  };

  if (hotspots?.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-6 md:p-8 text-center">
        <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-muted flex items-center justify-center mb-4">
          <Icon name="MapPin" size={32} className="text-muted-foreground" />
        </div>
        <h3 className="text-base md:text-lg font-semibold text-foreground mb-2">
          {t('catalogEditor.hotspotList.noHotspotsTitle')}
        </h3>
        <p className="text-sm text-muted-foreground max-w-xs">
          {t('catalogEditor.hotspotList.noHotspotsDesc')}
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <div className="px-4 md:px-6 py-3 md:py-4 border-b border-border">
        <div className="flex items-center justify-between">
          <h3 className="text-sm md:text-base font-semibold text-foreground">
            {t('catalogEditor.tabs.hotspots')}
          </h3>
          <span className="text-xs md:text-sm text-muted-foreground">
            {hotspots?.length} total
          </span>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto p-3 md:p-4 space-y-2">
        {hotspots?.map((hotspot) => (
          <div
            key={hotspot?.id}
            className={`p-3 md:p-4 rounded-lg border-2 transition-all duration-200 cursor-pointer ${
              selectedId === hotspot?.id
                ? 'border-accent bg-accent/10' :'border-border hover:border-accent/50 hover:bg-muted/50'
            }`}
            onClick={() => {
              if (onNavigateToPage) {
                onNavigateToPage(hotspot?.pageNumber);
              }
              onSelect(hotspot?.id);
            }}
          >
            <div className="flex items-start gap-3">
              <div className={`w-8 h-8 md:w-10 md:h-10 rounded-full ${getHotspotColor(hotspot?.type)} flex items-center justify-center flex-shrink-0`}>
                <Icon name={getHotspotIcon(hotspot?.type)} size={16} className="text-white" />
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="flex-1">
                    <h4 className="text-sm font-medium text-foreground truncate">
                      {getHotspotLabel(hotspot)}
                    </h4>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="inline-flex items-center gap-1 text-xs font-semibold text-accent bg-accent/10 px-2 py-1 rounded">
                        <Icon name="FileText" size={12} />
                        {t('catalogEditor.hotspotList.pageLabel', { number: hotspot?.pageNumber })}
                      </span>
                    </div>
                  </div>
                  {/*
                  <button
                    onClick={(e) => {
                      e?.stopPropagation();
                      onToggleVisibility(hotspot?.id);
                    }}
                    className="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded hover:bg-muted transition-base"
                    aria-label={hotspot?.visible ? 'Hide hotspot' : 'Show hotspot'}
                  >
                    <Icon 
                      name={hotspot?.visible ? 'Eye' : 'EyeOff'} 
                      size={14} 
                      className={hotspot?.visible ? 'text-foreground' : 'text-muted-foreground'}
                    />
                  </button>
                  */}

                </div>

                <div className="flex items-center gap-3 text-xs text-muted-foreground mb-2">
                  <span>X: {hotspot?.x?.toFixed(1)}Px</span>
                  <span>•</span>
                  <span>Y: {hotspot?.y?.toFixed(1)}Px</span>
                </div>

                {hotspot?.buttonText && (
                  <div className="text-xs text-muted-foreground truncate">
                    "{hotspot?.buttonText}"
                  </div>
                )}
              </div>
            </div>

            <div className="flex gap-2 mt-3">
              <Button
                variant="outline"
                size="sm"
                fullWidth
                iconName="Edit"
                iconPosition="left"
                onClick={(e) => {
                  e?.stopPropagation();
                  if (onNavigateToPage) {
                    onNavigateToPage(hotspot?.pageNumber);
                  }
                  onSelect(hotspot?.id);
                }}
              >
                {t('catalogEditor.hotspotList.edit')}
              </Button>
              <Button
                variant="outline"
                size="sm"
                fullWidth
                iconName="Copy"
                iconPosition="left"
                onClick={(e) => {
                  e?.stopPropagation();
                  onCopy?.(hotspot);
                }}
              >
                {t('catalogEditor.hotspotList.copy')}
              </Button>


              <Button
                variant="ghost"
                size="sm"
                iconName="Trash2"
                onClick={(e) => {
                  e?.stopPropagation();
                  onDelete(hotspot?.id);
                }}
                className="text-destructive hover:text-destructive"
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default HotspotList;